import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  Accordion,
  Anchor,
  CopyButton,
  Tooltip,
  Skeleton
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconCircleCheck,
  IconCircleX,
  IconPencil,
  IconHistory,
  IconLink,
  IconCheck,
  IconFileSpreadsheet
} from '@tabler/icons-react';
import DataTables from 'components/DataTables';
import Dates from 'components/Dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { masterPlanService } from 'services/dacum/masterPlan.service';
import { MasterPlanDetailResponse, CurriculumPayload } from 'types/dacum/masterPlan';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { pathnames } from 'routes/dacum';
import * as XLSX from 'xlsx';

interface MasterPlanDetailProps {
  jobFamily: string;
  year: string;
}

const extractKeyActionLines = (value: string): string[] =>
  value
    ? value
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
    : [];

const MasterPlanDetail = ({ jobFamily, year }: MasterPlanDetailProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectOpened, { open: openRejectModal, close: closeRejectModal }] = useDisclosure(false);
  const [versionOverviewOpened, { open: openVersionOverview, close: closeVersionOverview }] =
    useDisclosure(false);
  const [selectedMasterplanIndex, setSelectedMasterplanIndex] = React.useState(0);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const {
    data: responseData,
    isPending,
    error
  }: {
    data?: MasterPlanDetailResponse;
    isPending: boolean;
    error: unknown;
  } = useQuery({
    queryKey: ['masterPlanDetail', jobFamily, year],
    queryFn: () => masterPlanService.getMasterPlanDetail({ jobFamily, year }),
    enabled: Boolean(jobFamily && year)
  });

  // Extract the selected masterplan from the list
  const masterplanList = responseData?.listMasterplan || [];
  const data = masterplanList[selectedMasterplanIndex];
  const showNavigation = true; // Always show the sidebar

  const curriculumColumns = React.useMemo(
    () => [
      {
        heading: 'Competency',
        id: 'competencyName',
        sortable: true,
        active: true,
        props: (row: CurriculumPayload) => (
          <Stack gap={4}>
            <Text fw={600} fz={14} lineClamp={2}>
              {row.competencyName || '-'}
            </Text>
            <Group gap={4}>
              <Badge size='xs' variant='light' color='blue'>
                {row.competencyType || '-'}
              </Badge>
              <Badge size='xs' variant='light' color='grape'>
                Level {row.level || '-'}
              </Badge>
            </Group>
          </Stack>
        )
      },
      {
        heading: 'Category',
        id: 'category',
        sortable: true,
        active: true,
        props: (row: CurriculumPayload) => (
          <Tooltip
            label={row.categoryDescription || 'No description'}
            multiline
            w={300}
            withArrow
            disabled={!row.categoryDescription}
          >
            <Text fz={13} style={{ cursor: row.categoryDescription ? 'help' : 'default' }}>
              {row.category || '-'}
            </Text>
          </Tooltip>
        )
      },
      {
        heading: 'Job Positions',
        id: 'jobPositions',
        sortable: false,
        active: true,
        props: (row: CurriculumPayload) => {
          const positions = row.jobPositions || [];
          if (!Array.isArray(positions) || positions.length === 0) return '-';

          const positionNames = positions.map((pos: any) =>
            typeof pos === 'object' && pos?.name ? pos.name : pos
          );

          if (positionNames.length === 1) {
            return <Text fz={13}>{positionNames[0]}</Text>;
          }

          return (
            <Tooltip
              label={
                <Stack gap={4}>
                  {positionNames.map((name: string, idx: number) => (
                    <Text key={idx} fz={12}>
                      {idx + 1}. {name}
                    </Text>
                  ))}
                </Stack>
              }
              multiline
              w={280}
              withArrow
            >
              <Group gap={4} style={{ cursor: 'help' }}>
                <Text fz={13}>{positionNames[0]}</Text>
                <Badge size='xs' variant='filled' color='gray'>
                  +{positionNames.length - 1}
                </Badge>
              </Group>
            </Tooltip>
          );
        }
      },
      {
        heading: 'Courses',
        id: 'courses',
        sortable: false,
        active: true,
        props: (row: CurriculumPayload) => {
          const courses = row.courses || [];
          if (courses.length === 0) return '-';

          if (courses.length === 1) {
            const course = courses[0];
            return (
              <Anchor
                href={`/app/lcms/courses/${course.courseId}`}
                target='_blank'
                rel='noopener noreferrer'
                size='sm'
                lineClamp={2}
              >
                {course.courseName}
              </Anchor>
            );
          }

          return (
            <Tooltip
              label={
                <Stack gap={4}>
                  {courses.map(
                    (
                      course: { courseId: string; courseName: string; positions: string[] },
                      idx: number
                    ) => (
                      <Text key={course.courseId} fz={12}>
                        {idx + 1}. {course.courseName}
                      </Text>
                    )
                  )}
                </Stack>
              }
              multiline
              w={320}
              withArrow
            >
              <Group gap={4} style={{ cursor: 'help' }}>
                <Anchor
                  href={`/app/lcms/courses/${courses[0].courseId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  size='sm'
                  lineClamp={1}
                >
                  {courses[0].courseName}
                </Anchor>
                <Badge size='xs' variant='filled' color='teal'>
                  +{courses.length - 1}
                </Badge>
              </Group>
            </Tooltip>
          );
        }
      },
      {
        heading: 'Details',
        id: 'details',
        sortable: false,
        active: true,
        props: (row: CurriculumPayload) => (
          <Stack gap={2}>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                BU:
              </Text>
              <Text fz={12} lineClamp={1}>
                {row.businessUnitName || row.businessUnitId || '-'}
              </Text>
            </Group>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                Job Band:
              </Text>
              <Text fz={12}>{row.managementLevel || '-'}</Text>
            </Group>
            {row.schoolName && row.schoolName !== '-' && (
              <Group gap={4}>
                <Text fz={11} c='dimmed' fw={600}>
                  School:
                </Text>
                <Text fz={12} lineClamp={1}>
                  {row.schoolName}
                </Text>
              </Group>
            )}
          </Stack>
        )
      }
    ],
    []
  );

  // Prepare all versions data for overview table
  const allVersionsData = React.useMemo(() => {
    const versions: any[] = [];
    masterplanList.forEach((masterplan) => {
      if (masterplan.versions && masterplan.versions.length > 0) {
        masterplan.versions.forEach((version) => {
          versions.push({
            ...version,
            masterplanJobBand: masterplan.jobBand,
            masterplanBusinessUnit: masterplan.businessUnitName || masterplan.businessUnitId,
            masterplanId: masterplan.msDACUMProgramID
          });
        });
      }
    });
    // Sort by version number descending
    return versions.sort((a, b) => (b.versionNumber || 0) - (a.versionNumber || 0));
  }, [masterplanList]);

  // Version overview table columns
  const versionOverviewColumns = React.useMemo(
    () => [
      {
        heading: 'Version',
        id: 'versionNumber',
        sortable: true,
        active: true,
        props: (row: any) => (
          <Badge size='lg' radius='md' variant='gradient' gradient={{ from: 'blue', to: 'grape' }}>
            v{row.versionNumber}
          </Badge>
        )
      },
      {
        heading: 'Curriculum Name',
        id: 'programName',
        sortable: true,
        active: true,
        props: (row: any) => row.programName || '-'
      },
      {
        heading: 'Job Band',
        id: 'masterplanJobBand',
        sortable: true,
        active: true,
        props: (row: any) => row.masterplanJobBand || row.jobBand || '-'
      },
      {
        heading: 'Business Unit',
        id: 'masterplanBusinessUnit',
        sortable: true,
        active: true,
        props: (row: any) =>
          row.masterplanBusinessUnit || row.businessUnitName || row.businessUnitId || '-'
      },
      {
        heading: 'Status',
        id: 'status',
        sortable: true,
        active: true,
        props: (row: any) => {
          const status = row.status || 'Pending';
          const colorMap: Record<string, string> = {
            approved: 'teal',
            rejected: 'red',
            pending: 'yellow',
            draft: 'gray'
          };
          return (
            <Badge color={colorMap[status.toLowerCase()] || 'gray'} variant='filled' radius='md'>
              {status}
            </Badge>
          );
        }
      },
      {
        heading: 'Curriculum Items',
        id: 'curriculumCount',
        sortable: true,
        active: true,
        props: (row: any) => row.curriculum?.length || 0
      },
      {
        heading: 'Courses',
        id: 'coursesCount',
        sortable: true,
        active: true,
        props: (row: any) => row.courses?.length || 0
      },
      {
        heading: 'Created Date',
        id: 'createdAt',
        sortable: true,
        active: true,
        props: (row: any) => (
          <Text fz={13}>
            <Dates date={row.createdAt} dateFormat='dateNameTime' />
          </Text>
        )
      }
    ],
    []
  );

  const versionCurriculumColumns = React.useMemo(
    () => [
      {
        heading: 'Competency',
        id: 'competencyName',
        sortable: true,
        active: true,
        props: (row: any) => (
          <Stack gap={4}>
            <Text fw={600} fz={14} lineClamp={2}>
              {row.competencyName || '-'}
            </Text>
            <Group gap={4}>
              <Badge size='xs' variant='light' color='grape'>
                Level {row.level || '-'}
              </Badge>
              {row.category && (
                <Badge size='xs' variant='light' color='blue'>
                  {row.category}
                </Badge>
              )}
            </Group>
          </Stack>
        )
      },
      {
        heading: 'Job Positions',
        id: 'jobPositionId',
        sortable: false,
        active: true,
        props: (row: any) => {
          // Handle both string and array formats
          if (row.jobPositions && Array.isArray(row.jobPositions)) {
            if (row.jobPositions.length > 0) {
              const positionNames = row.jobPositions.map((pos: any) =>
                typeof pos === 'object' && pos?.name ? pos.name : pos
              );

              if (positionNames.length === 1) {
                return <Text fz={13}>{positionNames[0]}</Text>;
              }

              return (
                <Tooltip
                  label={
                    <Stack gap={4}>
                      {positionNames.map((name: string, idx: number) => (
                        <Text key={idx} fz={12}>
                          {idx + 1}. {name}
                        </Text>
                      ))}
                    </Stack>
                  }
                  multiline
                  w={280}
                  withArrow
                >
                  <Group gap={4} style={{ cursor: 'help' }}>
                    <Text fz={13}>{positionNames[0]}</Text>
                    <Badge size='xs' variant='filled' color='gray'>
                      +{positionNames.length - 1}
                    </Badge>
                  </Group>
                </Tooltip>
              );
            }
          }
          // Fallback to jobPositionId if jobPositions is not available
          return <Text fz={13}>{row.jobPositionId || '-'}</Text>;
        }
      },
      {
        heading: 'Details',
        id: 'details',
        sortable: false,
        active: true,
        props: (row: any) => (
          <Stack gap={2}>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                BU:
              </Text>
              <Text fz={12}>{row.businessUnitId || '-'}</Text>
            </Group>
            <Group gap={4}>
              <Text fz={11} c='dimmed' fw={600}>
                Job Band:
              </Text>
              <Text fz={12}>{row.jobBand || '-'}</Text>
            </Group>
            {row.description && (
              <Tooltip label={row.description} multiline w={300} withArrow>
                <Text fz={12} c='dimmed' lineClamp={1} style={{ cursor: 'help' }}>
                  {row.description}
                </Text>
              </Tooltip>
            )}
          </Stack>
        )
      }
    ],
    []
  );

  // eslint-disable-next-line no-unused-vars
  const keyActionLines = React.useMemo(
    () => (data ? extractKeyActionLines(data.keyAction) : []),
    [data]
  );

  const approveMasterPlanMutation = useMutation({
    mutationFn: () => masterPlanService.approveMasterPlan(data?.msDACUMProgramID || ''),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Approved',
        message: response?.message || 'Master plan has been approved successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['masterPlanList'] });
      queryClient.invalidateQueries({ queryKey: ['masterPlanDetail', jobFamily, year] });
    },
    onError: (mutationError: any) => {
      showErrorNotification({
        message:
          mutationError?.response?.data?.message ||
          mutationError?.message ||
          'Unable to approve master plan. Please try again.'
      });
    }
  });

  const rejectMasterPlanMutation = useMutation({
    mutationFn: () =>
      masterPlanService.rejectMasterPlan({
        msDACUMProgramID: data?.msDACUMProgramID || '',
        reason: rejectReason.trim()
      }),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Rejected',
        message: response?.message || 'Master plan has been rejected successfully.'
      });
      setRejectReason('');
      closeRejectModal();
      queryClient.invalidateQueries({ queryKey: ['masterPlanList'] });
      queryClient.invalidateQueries({ queryKey: ['masterPlanDetail', jobFamily, year] });
    },
    onError: (mutationError: any) => {
      showErrorNotification({
        message:
          mutationError?.response?.data?.message ||
          mutationError?.message ||
          'Unable to reject master plan. Please try again.'
      });
    }
  });

  const deleteMasterPlanMutation = useMutation({
    mutationFn: () => masterPlanService.deleteMasterPlan(data?.msDACUMProgramID || ''),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Deleted',
        message: response?.message || 'Master plan has been deleted successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['masterPlanList'] });
      navigate(pathnames.reviewMasterPlan);
    },
    onError: (mutationError: any) => {
      showErrorNotification({
        message:
          mutationError?.response?.data?.message ||
          mutationError?.message ||
          'Unable to delete master plan. Please try again.'
      });
    }
  });

  // Now conditional returns are safe
  if (!jobFamily || !year) {
    return <Alert color='red'>Master plan job family and year are required.</Alert>;
  }

  if (isPending) {
    return (
      <Flex gap='xl' direction={{ base: 'column', md: 'row' }}>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Stack gap='xl'>
            {/* Action Buttons Skeleton */}
            <Group gap='sm' wrap='wrap'>
              <Skeleton height={36} width={140} radius='md' />
              <Skeleton height={36} width={140} radius='md' />
              <Skeleton height={36} width={120} radius='md' />
            </Group>

            {/* Header Skeleton */}
            <Paper p='xl' radius='lg' withBorder>
              <Stack gap='md'>
                <Group justify='space-between' align='flex-start'>
                  <Stack gap={4}>
                    <Skeleton height={32} width={300} />
                    <Skeleton height={20} width={200} />
                  </Stack>
                  <Skeleton height={32} width={100} radius='md' />
                </Group>
              </Stack>
            </Paper>

            {/* Overview Skeleton */}
            <Card radius='lg' withBorder>
              <Stack gap='lg'>
                <Skeleton height={24} width={120} />
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '16px'
                  }}
                >
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Paper key={i} withBorder radius='md' p='md'>
                      <Skeleton height={16} width='60%' mb={8} />
                      <Skeleton height={20} width='80%' />
                    </Paper>
                  ))}
                </Box>
              </Stack>
            </Card>

            {/* Competency List Skeleton */}
            <Card radius='lg' withBorder>
              <Stack gap='md'>
                <Group justify='space-between'>
                  <Skeleton height={24} width={180} />
                  <Skeleton height={32} width={100} radius='md' />
                </Group>
                <Stack gap='sm'>
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} height={60} radius='md' />
                  ))}
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Box>

        {/* Sidebar Skeleton */}
        <Box style={{ width: isMobile ? '100%' : '320px', flexShrink: 0 }}>
          <Paper withBorder radius='lg' p='lg'>
            <Skeleton height={24} width='70%' mb='lg' />
            <Stack gap='sm'>
              {[1, 2].map((i) => (
                <Skeleton key={i} height={120} radius='lg' />
              ))}
            </Stack>
          </Paper>
        </Box>
      </Flex>
    );
  }

  if (error) {
    return <Alert color='red'>Unable to load master plan detail. Please try again later.</Alert>;
  }

  if (!data) {
    return <Alert color='yellow'>Master plan detail is not available.</Alert>;
  }

  const canModify = !data.status;
  const isApproved = data.status?.toLowerCase() === 'approved';
  const isRejected = data.status?.toLowerCase() === 'rejected';
  const hasVersions = data.versions && data.versions.length > 0;
  const isActionLoading =
    approveMasterPlanMutation.isPending ||
    rejectMasterPlanMutation.isPending ||
    deleteMasterPlanMutation.isPending;

  const statusLabel = data.status || 'Pending';
  const statusColorMap: Record<string, string> = {
    approved: 'teal',
    rejected: 'red',
    pending: 'yellow',
    draft: 'gray'
  };
  const statusColor = statusColorMap[statusLabel.toLowerCase()] || 'gray';

  const overviewItems = [
    { label: 'Job Family', value: data.jobFamily || '-' },
    { label: 'Job Band', value: data.jobBand || '-' },
    { label: 'Business Unit', value: data.businessUnitName || data.businessUnitId || '-' },
    { label: 'Year', value: data.year?.toString() || '-' },
    { label: 'Curriculum Items', value: data.curriculumList.length.toString() }
  ];

  const handleApprove = () => {
    if (!canModify) return;
    approveMasterPlanMutation.mutate();
  };

  const handleRejectSubmit = () => {
    if (!canModify) return;
    if (!rejectReason.trim()) {
      showErrorNotification({ message: 'Please provide a rejection reason.' });
      return;
    }
    rejectMasterPlanMutation.mutate();
  };

  const handleRejectModalClose = () => {
    if (rejectMasterPlanMutation.isPending) {
      return;
    }
    setRejectReason('');
    closeRejectModal();
  };

  const handleExportToExcel = () => {
    if (!masterplanList || masterplanList.length === 0) return;

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Process each masterplan and create a separate sheet
    masterplanList.forEach((masterplan, index) => {
      const excelData = masterplan.curriculumList.flatMap((curriculum) => {
        // If no courses, create one row with empty course fields
        if (!curriculum.courses || curriculum.courses.length === 0) {
          return [
            {
              'Competency Type': curriculum.competencyType || '-',
              'Competency Name': curriculum.competencyName || '-',
              'Competency Description': '-',
              Kategori: curriculum.category || '-',
              'Category Description': curriculum.categoryDescription || '-',
              Level: curriculum.level?.toString() || '-',
              'Level Description': curriculum.levelDescription || '-',
              Module: '-',
              LO: '-',
              Content: '-',
              'Learning Hours': '-',
              'Mode of Delivery': '-',
              LIM: '-',
              LIA: '-',
              'Job Positions':
                curriculum.jobPositions && curriculum.jobPositions.length > 0
                  ? curriculum.jobPositions
                      .map((pos: any) => (typeof pos === 'object' && pos?.name ? pos.name : pos))
                      .join(', ')
                  : '-',
              'Job Band': curriculum.managementLevel || '-',
              'Job Fam': curriculum.jobFamilyName || '-',
              'Job Fam Group': curriculum.schoolName || '-',
              'Divisi / Unit': curriculum.businessUnitName || '-'
            }
          ];
        }

        // Create a row for each course
        return curriculum.courses.map((course) => ({
          'Competency Type': curriculum.competencyType || '-',
          'Competency Name': curriculum.competencyName || '-',
          'Competency Description': '-',
          Kategori: curriculum.category || '-',
          'Category Description': curriculum.categoryDescription || '-',
          Level: curriculum.level?.toString() || '-',
          'Level Description': curriculum.levelDescription || '-',
          Module: course.courseName || '-',
          LO: course.learningObjectives || '-',
          Content: '-',
          'Learning Hours': course.learningHours?.toString() || '-',
          'Mode of Delivery': course.modeOfDelivery || '-',
          LIM: course.C || '-',
          LIA: course.flaggingLIA ? 'Yes' : 'No',
          'Job Positions':
            course.positions && course.positions.length > 0
              ? course.positions.join(', ')
              : curriculum.jobPositions && curriculum.jobPositions.length > 0
                ? curriculum.jobPositions
                    .map((pos: any) => (typeof pos === 'object' && pos?.name ? pos.name : pos))
                    .join(', ')
                : '-',
          'Job Band': curriculum.managementLevel || '-',
          'Job Fam': curriculum.jobFamilyName || '-',
          'Job Fam Group': curriculum.schoolName || '-',
          'Divisi / Unit': curriculum.businessUnitName || '-'
        }));
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      worksheet['!cols'] = [
        { wch: 20 }, // Competency Type
        { wch: 40 }, // Competency Name
        { wch: 30 }, // Competency Description
        { wch: 20 }, // Kategori
        { wch: 50 }, // Category Description
        { wch: 12 }, // Level
        { wch: 50 }, // Level Description
        { wch: 40 }, // Module
        { wch: 50 }, // LO
        { wch: 20 }, // Content
        { wch: 15 }, // Learning Hours
        { wch: 20 }, // Mode of Delivery
        { wch: 15 }, // LIM
        { wch: 12 }, // LIA
        { wch: 40 }, // Job Family
        { wch: 15 }, // Job Band
        { wch: 30 }, // Job Fam
        { wch: 30 }, // Job Fam Group
        { wch: 30 } // Divisi / Unit
      ];

      const businessUnit = masterplan.businessUnitName || masterplan.businessUnitId || 'Unknown';
      const jobBand = masterplan.jobBand || 'Unknown';
      const baseName = `${businessUnit} - ${jobBand}`;
      const sheetName = `${index + 1}. ${baseName}`.substring(0, 31).replace(/[\\/?*[\]]/g, '');

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    // Generate filename with job family, year and date
    const fileName = `MasterPlan_${jobFamily}_${year}_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Write file
    XLSX.writeFile(workbook, fileName);

    showSuccessNotification({
      title: 'Export Successful',
      message: 'Master plan has been exported to Excel successfully.'
    });
  };

  return (
    <Flex gap='xl' direction={{ base: 'column', md: 'row' }}>
      {/* Main Content */}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Stack gap='xl'>
          {/* Action Buttons */}
          <Flex justify='space-between' align='center' wrap='wrap' gap='md'>
            <CopyButton
              value={`${window.location.origin}/app/dacum/masterplan/shared/${data.msDACUMProgramID}?jobfamily=${encodeURIComponent(jobFamily)}&year=${year}`}
            >
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied!' : 'Copy share link'} withArrow position='right'>
                  <Button
                    variant='light'
                    size='md'
                    color={copied ? 'teal' : 'blue'}
                    leftSection={copied ? <IconCheck size={18} /> : <IconLink size={18} />}
                    onClick={copy}
                  >
                    {copied ? 'Link Copied!' : 'Copy Share Link'}
                  </Button>
                </Tooltip>
              )}
            </CopyButton>

            <Group gap='sm' wrap='wrap'>
              <Button
                variant='light'
                size='md'
                color='violet'
                leftSection={<IconHistory size={18} />}
                onClick={openVersionOverview}
                disabled={isActionLoading}
              >
                View All Versions
              </Button>
              <Button
                variant='light'
                size='md'
                color='green'
                leftSection={<IconFileSpreadsheet size={18} />}
                onClick={handleExportToExcel}
                disabled={isActionLoading}
              >
                Export to Excel
              </Button>
              <Button
                variant='light'
                size='md'
                leftSection={<IconPencil size={18} />}
                onClick={() => {
                  const editUrl = pathnames.editMasterPlan.replace(':id', data.msDACUMProgramID);
                  const queryParams = new URLSearchParams();
                  queryParams.append('jobfamily', data.jobFamily);
                  queryParams.append('year', (data.year || new Date().getFullYear()).toString());
                  queryParams.append('masterplanId', data.msDACUMProgramID);
                  navigate(`${editUrl}?${queryParams.toString()}`);
                }}
                disabled={isActionLoading}
              >
                {isApproved || isRejected ? 'Create Revision' : 'Update'}
              </Button>
              {canModify && (
                <>
                  <Button
                    variant='light'
                    color='teal'
                    size='md'
                    leftSection={<IconCircleCheck size={18} />}
                    onClick={handleApprove}
                    loading={approveMasterPlanMutation.isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    variant='light'
                    color='orange'
                    size='md'
                    leftSection={<IconCircleX size={18} />}
                    onClick={openRejectModal}
                    loading={rejectMasterPlanMutation.isPending}
                  >
                    Reject
                  </Button>
                </>
              )}
            </Group>
          </Flex>

          {/* Modern Header */}
          <Paper
            p='xl'
            radius='lg'
            withBorder
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            <Stack gap='md'>
              <Group justify='space-between' align='flex-start'>
                <Stack gap={4}>
                  <Text fw={700} fz={28} c='white'>
                    {data.programName}
                  </Text>
                  <Text fz={15} c='white' opacity={0.9}>
                    {data.jobFamily} • {data.jobBand || 'N/A'}
                  </Text>
                </Stack>
                <Badge color={statusColor} variant='filled' size='lg' radius='md'>
                  {statusLabel}
                </Badge>
              </Group>
            </Stack>
          </Paper>

          <Card radius='lg' withBorder>
            <Stack gap='lg'>
              <Text fw={700} fz={18} c='blue'>
                Overview
              </Text>

              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '16px'
                }}
              >
                {overviewItems.map((item) => (
                  <Paper key={item.label} withBorder radius='md' p='md'>
                    <Text fz={12} fw={600} c='dimmed' tt='uppercase'>
                      {item.label}
                    </Text>
                    <Text fw={600}>{item.value}</Text>
                  </Paper>
                ))}
              </Box>

              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '16px'
                }}
              >
                <Paper withBorder radius='md' p='md'>
                  <Stack gap={6}>
                    <Text fw={600}>Description</Text>
                    <Text fz={14}>{data.description || '-'}</Text>
                  </Stack>
                </Paper>
                {/* Key Actions section removed from display but still in data */}
              </Box>
            </Stack>
          </Card>

          <Card radius='lg' withBorder>
            <Stack gap='md'>
              <Group justify='space-between' align='center'>
                <Text fw={700} fz={18} c='blue'>
                  Competency List
                </Text>
                <Badge color='grape' variant='light' size='lg' radius='md'>
                  {data.curriculumList.length} item{data.curriculumList.length === 1 ? '' : 's'}
                </Badge>
              </Group>
              <DataTables
                columns={curriculumColumns}
                data={data.curriculumList}
                loading={false}
                withPagination={false}
                emptyMessageTitle='No curriculum data'
                emptyMessageDescription='Curriculum entries will appear after they are linked to this master plan.'
              />
            </Stack>
          </Card>

          {hasVersions && (
            <Card radius='lg' withBorder>
              <Stack gap='md'>
                <Group gap='md' align='center'>
                  <Box
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <IconHistory size={20} color='white' />
                  </Box>
                  <Text fw={700} fz={18} c='blue'>
                    Version History
                  </Text>
                  <Badge color='blue' variant='light' size='lg' radius='md'>
                    {data.versions.length} version{data.versions.length === 1 ? '' : 's'}
                  </Badge>
                </Group>

                <Accordion variant='separated' radius='md' chevronPosition='left'>
                  {data.versions.map((version) => (
                    <Accordion.Item
                      key={version.versionNumber}
                      value={`version-${version.versionNumber}`}
                      style={{ border: '2px solid #e9ecef', borderRadius: '8px' }}
                    >
                      <Accordion.Control>
                        <Group justify='space-between' wrap='nowrap' pr='md'>
                          <Group gap='md'>
                            <Badge
                              size='xl'
                              radius='md'
                              variant='gradient'
                              gradient={{ from: 'blue', to: 'grape' }}
                            >
                              v{version.versionNumber}
                            </Badge>
                            <Stack gap={2}>
                              <Text fw={600} fz={15}>
                                {version.programName}
                              </Text>
                              <Text fz={12} c='dimmed'>
                                <Dates date={version.createdAt} dateFormat='dateNameTime' />
                              </Text>
                            </Stack>
                          </Group>
                          {version.status && (
                            <Badge
                              color={statusColorMap[version.status.toLowerCase()] || 'gray'}
                              variant='filled'
                              size='md'
                              radius='md'
                            >
                              {version.status}
                            </Badge>
                          )}
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap='lg'>
                          {/* Version Details Grid */}
                          <Box
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                              gap: '12px'
                            }}
                          >
                            <Paper
                              withBorder
                              radius='md'
                              p='md'
                              style={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                              }}
                            >
                              <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                Job Family
                              </Text>
                              <Text fz={14} fw={600}>
                                {version.jobFamily}
                              </Text>
                            </Paper>
                            <Paper
                              withBorder
                              radius='md'
                              p='md'
                              style={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                              }}
                            >
                              <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                Job Band
                              </Text>
                              <Text fz={14} fw={600}>
                                {version.jobBand || 'N/A'}
                              </Text>
                            </Paper>
                            <Paper
                              withBorder
                              radius='md'
                              p='md'
                              style={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                              }}
                            >
                              <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                Business Unit
                              </Text>
                              <Text fz={14} fw={600}>
                                {version.businessUnitName || version.businessUnitId || '-'}
                              </Text>
                            </Paper>
                            <Paper
                              withBorder
                              radius='md'
                              p='md'
                              style={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                              }}
                            >
                              <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                Curriculum Items
                              </Text>
                              <Text fz={14} fw={600}>
                                {version.curriculum?.length || 0}
                              </Text>
                            </Paper>
                            <Paper
                              withBorder
                              radius='md'
                              p='md'
                              style={{
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                              }}
                            >
                              <Text fz={11} fw={700} c='dimmed' tt='uppercase' mb={4}>
                                Courses
                              </Text>
                              <Text fz={14} fw={600}>
                                {version.courses?.length || 0}
                              </Text>
                            </Paper>
                          </Box>

                          {/* Description */}
                          {version.description && (
                            <Paper withBorder radius='md' p='md' bg='blue.0'>
                              <Text fz={12} fw={700} c='blue.7' tt='uppercase' mb={8}>
                                Description
                              </Text>
                              <Text fz={14} fw={500}>
                                {version.description}
                              </Text>
                            </Paper>
                          )}
                          {/* Key Actions section removed from version history display */}

                          {/* Courses List */}
                          {version.courses && version.courses.length > 0 && (
                            <Paper withBorder radius='md' p='md' bg='teal.0'>
                              <Text fz={12} fw={700} c='teal.7' tt='uppercase' mb={8}>
                                Courses ({version.courses.length})
                              </Text>
                              <Stack gap={4}>
                                {version.courses.map((course: any, courseIndex: number) => (
                                  <Text key={course.courseId || courseIndex} fz={14} fw={500}>
                                    {courseIndex + 1}. {course.courseName || course.name || '-'}
                                  </Text>
                                ))}
                              </Stack>
                            </Paper>
                          )}

                          {/* Curriculum Table */}
                          {version.curriculum && version.curriculum.length > 0 && (
                            <Box>
                              <Group mb='sm' align='center'>
                                <Text fw={700} fz={16} c='grape.8'>
                                  Curriculum Details
                                </Text>
                                <Badge color='grape' variant='light' radius='md'>
                                  {version.curriculum.length} items
                                </Badge>
                              </Group>
                              <DataTables
                                columns={versionCurriculumColumns}
                                data={version.curriculum}
                                loading={false}
                                withPagination={false}
                                emptyMessageTitle='No curriculum'
                                emptyMessageDescription='No curriculum entries for this version.'
                              />
                            </Box>
                          )}

                          {/* Approval History */}
                          {version.approvalHistory && version.approvalHistory.length > 0 && (
                            <Paper withBorder radius='md' p='md' bg='yellow.0'>
                              <Text fz={12} fw={700} c='yellow.9' tt='uppercase' mb={8}>
                                Approval History ({version.approvalHistory.length})
                              </Text>
                              <Stack gap={8}>
                                {version.approvalHistory.map(
                                  (approval: any, approvalIndex: number) => (
                                    <Box key={approvalIndex}>
                                      <Text fz={13} fw={600}>
                                        {approval.action || 'Action'} by{' '}
                                        {approval.approver || 'Unknown'}
                                      </Text>
                                      {approval.date && (
                                        <Text fz={12} c='dimmed'>
                                          <Dates date={approval.date} dateFormat='dateNameTime' />
                                        </Text>
                                      )}
                                      {approval.comment && (
                                        <Text fz={12} mt={4}>
                                          {approval.comment}
                                        </Text>
                                      )}
                                    </Box>
                                  )
                                )}
                              </Stack>
                            </Paper>
                          )}
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Stack>
            </Card>
          )}

          <Modal
            opened={rejectOpened}
            onClose={handleRejectModalClose}
            title='Reject Master Plan'
            centered
          >
            <Stack gap='md'>
              <Textarea
                label='Reason'
                placeholder='Enter rejection reason'
                minRows={3}
                value={rejectReason}
                onChange={(event) => setRejectReason(event.currentTarget.value)}
                disabled={rejectMasterPlanMutation.isPending}
              />
              <Group justify='flex-end'>
                <Button
                  variant='default'
                  onClick={handleRejectModalClose}
                  disabled={rejectMasterPlanMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  color='orange'
                  leftSection={<IconCircleX size={16} />}
                  onClick={handleRejectSubmit}
                  loading={rejectMasterPlanMutation.isPending}
                >
                  Submit
                </Button>
              </Group>
            </Stack>
          </Modal>

          {/* Version Overview Modal */}
          <Modal
            opened={versionOverviewOpened}
            onClose={closeVersionOverview}
            title={
              <Group gap='sm'>
                <Box
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <IconHistory size={20} color='white' />
                </Box>
                <Text fw={700} fz={18}>
                  All Versions Overview
                </Text>
                <Badge color='violet' variant='light' size='lg' radius='md'>
                  {allVersionsData.length} version{allVersionsData.length === 1 ? '' : 's'}
                </Badge>
              </Group>
            }
            size='95%'
            centered
          >
            <Stack gap='md'>
              <Paper p='sm' withBorder bg='blue.0'>
                <Text fz={14} c='blue.9'>
                  View all versions across all master plan configurations for {jobFamily} ({year})
                </Text>
              </Paper>
              <DataTables
                columns={versionOverviewColumns}
                data={allVersionsData}
                loading={false}
                withPagination={true}
                emptyMessageTitle='No versions found'
                emptyMessageDescription='No version history available for these master plans.'
              />
            </Stack>
          </Modal>
        </Stack>
      </Box>

      {/* Navigation Sidebar - Always show */}
      {showNavigation && (
        <Box
          style={{
            width: isMobile ? '100%' : '320px',
            flexShrink: 0
          }}
        >
          <Paper
            withBorder
            radius='lg'
            p='lg'
            shadow='sm'
            style={{
              background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
              ...(isMobile ? {} : { position: 'sticky', top: '20px' })
            }}
          >
            <Group gap='xs' mb='lg'>
              <IconHistory size={20} style={{ color: '#228be6' }} />
              <Text fw={700} fz={17} c='blue.7'>
                Available Master Plans
              </Text>
            </Group>
            {masterplanList.length === 1 ? (
              <Paper withBorder radius='md' p='md' bg='blue.0'>
                <Stack gap={8}>
                  <Text fz={13} fw={600} c='blue.9'>
                    Single Configuration
                  </Text>
                  <Text fz={12} c='dimmed'>
                    This job family only has one master plan configuration.
                  </Text>
                </Stack>
              </Paper>
            ) : (
              <>
                <Text fz={12} c='dimmed' mb='md'>
                  Select a configuration to view details
                </Text>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'row' : 'column',
                    gap: '12px',
                    overflowX: isMobile ? 'auto' : 'hidden',
                    overflowY: isMobile ? 'hidden' : 'auto',
                    maxHeight: isMobile ? 'none' : '600px',
                    paddingBottom: isMobile ? '8px' : '0px',
                    paddingRight: isMobile ? '0px' : '4px'
                  }}
                >
                  {masterplanList.map((masterplan, index) => {
                    const isSelected = selectedMasterplanIndex === index;
                    return (
                      <Paper
                        key={index}
                        p='md'
                        withBorder
                        radius='lg'
                        shadow={isSelected ? 'md' : 'xs'}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#e7f5ff' : '#ffffff',
                          borderColor: isSelected ? '#228be6' : '#dee2e6',
                          borderWidth: isSelected ? '2px' : '1px',
                          transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                          transition: 'all 0.2s ease',
                          minWidth: isMobile ? '240px' : 'unset',
                          flexShrink: isMobile ? 0 : 1
                        }}
                        onClick={() => setSelectedMasterplanIndex(index)}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.transform = 'scale(1.01)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                        <Stack gap={6}>
                          <Group justify='space-between' wrap='nowrap'>
                            <Text
                              fw={700}
                              fz={15}
                              c={isSelected ? 'blue.9' : 'dark.7'}
                              lineClamp={1}
                            >
                              {masterplan.jobBand}
                            </Text>
                            {isSelected && (
                              <IconCheck size={18} style={{ color: '#228be6', flexShrink: 0 }} />
                            )}
                          </Group>
                          <Text fz={13} c={isSelected ? 'blue.7' : 'dimmed'} lineClamp={1}>
                            {masterplan.jobFamily}
                          </Text>
                          <Text fz={12} c='dimmed' lineClamp={1}>
                            {masterplan.businessUnitName || masterplan.businessUnitId}
                          </Text>
                          <Group gap='xs' mt={4}>
                            <Badge
                              size='sm'
                              radius='md'
                              variant={isSelected ? 'filled' : 'light'}
                              color={
                                masterplan.status?.toLowerCase() === 'approved'
                                  ? 'teal'
                                  : masterplan.status?.toLowerCase() === 'rejected'
                                    ? 'red'
                                    : 'yellow'
                              }
                            >
                              {masterplan.status || 'Pending'}
                            </Badge>
                            <Badge size='sm' radius='md' variant='light' color='gray'>
                              {masterplan.curriculumList?.length || 0} items
                            </Badge>
                          </Group>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              </>
            )}
          </Paper>
        </Box>
      )}
    </Flex>
  );
};

export default MasterPlanDetail;
